import { newId } from '../lib/ids'
import { KINDS, titleField, type EntityKind } from '../lib/model'
import { linkedOfKind } from '../lib/reducer'
import { useStore } from '../lib/store'
import { AddRecord } from './AddRecord'
import { EntityLink } from './EntityLink'
import { Icon, iconName } from './Icon'

/** Edit a many-to-many relationship (backed by bidirectional links): list the
 *  linked entities of a kind, add an existing one, or create-and-link a new one.
 *
 *  The picker itself is `AddRecord`, shared with the section headings on the client
 *  and project pages — the aside and the sections were two implementations of the
 *  same control and only one of them could search. */
export function RelationField({
  entityId,
  targetKind,
}: {
  entityId: string
  targetKind: EntityKind
}) {
  const { state, dispatch } = useStore()
  const def = KINDS[targetKind]
  const rec = state.entities[entityId]
  if (!rec) return null

  const linked = linkedOfKind(state, entityId, targetKind)

  const link = (toId: string) => {
    void dispatch({ type: 'link.add', subject: entityId, data: { to: toId } })
  }
  const createAndLink = (name: string) => {
    const id = newId(def.prefix)
    void dispatch([
      { type: `${targetKind}.create`, subject: id, data: { [titleField(targetKind)]: name } },
      { type: 'link.add', subject: entityId, data: { to: id } },
    ])
  }

  return (
    <div className="space-y-1.5">
      {linked.length > 0 && (
        <ul className="rows">
          {linked.map((e) => (
            <li key={e.id} className="text-body flex items-center gap-2 py-1.5">
              <Icon name={iconName(def.icon)} size={14} style={{ color: 'var(--muted)' }} />
              <EntityLink id={e.id} label={String(e.fields[titleField(targetKind)] ?? '')} />
              {typeof e.fields.role === 'string' && e.fields.role && (
                <span className="text-meta" style={{ color: 'var(--muted)' }}>
                  {e.fields.role}
                </span>
              )}
              <button
                type="button"
                onClick={() =>
                  void dispatch({ type: 'link.remove', subject: entityId, data: { to: e.id } })
                }
                className="ml-auto opacity-40 hover:opacity-100"
                aria-label={`Remove ${String(e.fields[titleField(targetKind)] ?? '')}`}
                title="Remove"
              >
                <Icon name="X" size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <AddRecord kind={targetKind} exclude={rec.links} onPick={link} onCreate={createAndLink} />
    </div>
  )
}
